import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  User, 
  Mail, 
  Phone, 
  Tag, 
  Camera, 
  Save, 
  X, 
  Calendar,
  Shield,
  Edit2,
  CheckCircle,
  XCircle,
  Briefcase,
  Upload,
  Trash2,
  Loader
} from 'lucide-react';
import useAuthStore from '../../store/authStore';
import { toast } from 'react-hot-toast';

const profileSchema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  bio: yup.string().max(500, 'Bio must be less than 500 characters'),
});

const businessTagsOptions = [
  'Data Reseller',
  'Tech',
  'Logistics',
  'Retail',
  'E-commerce',
  'Services',
  'Manufacturing',
  'Agriculture',
  'Healthcare',
  'Education',
];

const ProfilePage = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  
  const { user, updateProfile, isLoading, uploadAvatar, deleteAvatar } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      bio: user?.bio || '',
    },
  });

  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName,
        lastName: user.lastName,
        bio: user.bio || '',
      });
      setSelectedTags(user.businessTags || []);
    }
  }, [user, reset]);

  const toggleTag = (tag) => {
    const newTags = selectedTags.includes(tag)
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag];
    setSelectedTags(newTags);
  };

  const onSubmit = async (data) => {
    try {
      await updateProfile({
        ...data,
        businessTags: selectedTags,
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Update profile error:', error);
    }
  };

  const handleCancel = () => {
    reset({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      bio: user?.bio || '',
    });
    setSelectedTags(user?.businessTags || []);
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPEG, PNG, GIF, WEBP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);

    try {
      setUploading(true);
      await uploadAvatar(file);
      setPreviewUrl(null);
    } catch (error) {
      console.error('Upload error:', error);
      setPreviewUrl(null);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteAvatar = async () => {
    if (!user?.avatar) return;
    
    if (window.confirm('Are you sure you want to remove your profile picture?')) {
      try {
        setUploading(true);
        await deleteAvatar();
      } catch (error) {
        console.error('Delete error:', error);
      } finally {
        setUploading(false);
      }
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-[#E8ECF5] rounded-xl p-4 sm:p-5 shadow-neomorph border border-primary-100/50">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center">
            <div className="w-1 h-6 sm:h-8 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-3" />
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-800">Profile</h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                Manage your personal information and preferences
              </p>
            </div>
          </div>
          
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all flex items-center justify-center"
            >
              <Edit2 className="h-4 w-4 mr-2" />
              Edit Profile
            </button>
          ) : (
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center justify-center"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </button>
              <button
                onClick={handleSubmit(onSubmit)}
                disabled={isLoading}
                className="px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all flex items-center justify-center disabled:opacity-50"
              >
                <Save className="h-4 w-4 mr-2" />
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
        {/* Left Column - Profile Info */}
        <div className="lg:flex-1 lg:order-1 space-y-4 sm:space-y-6">
          {/* Personal Information Card */}
          <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-6 border border-primary-100/30">
            <div className="flex items-center mb-4 sm:mb-6">
              <div className="p-2 bg-primary-50 rounded-lg mr-3">
                <User className="h-5 w-5 text-primary-600" />
              </div>
              <h2 className="text-base sm:text-lg font-semibold text-gray-800">Personal Information</h2>
            </div>

            <div className="space-y-4 sm:space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        {...register('firstName')}
                        className={`w-full px-3 sm:px-4 py-2 bg-white rounded-lg shadow-neomorph-inset border ${
                          errors.firstName ? 'border-red-300' : 'border-primary-100/30'
                        } focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base`}
                      />
                      {errors.firstName && (
                        <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.firstName.message}</p>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-900 font-medium text-sm sm:text-base">{user?.firstName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        {...register('lastName')}
                        className={`w-full px-3 sm:px-4 py-2 bg-white rounded-lg shadow-neomorph-inset border ${
                          errors.lastName ? 'border-red-300' : 'border-primary-100/30'
                        } focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base`}
                      />
                      {errors.lastName && (
                        <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.lastName.message}</p>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-900 font-medium text-sm sm:text-base">{user?.lastName}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <div className="flex flex-col sm:flex-row sm:items-center p-3 bg-gray-50 rounded-lg shadow-neomorph-inset gap-2 sm:gap-0">
                  <div className="flex items-center flex-1 min-w-0">
                    <Mail className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                    <p className="text-gray-900 text-sm sm:text-base break-all">{user?.email}</p>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 w-fit">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Verified
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="flex items-center p-3 bg-gray-50 rounded-lg shadow-neomorph-inset">
                  <Phone className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                  <p className="text-gray-900 text-sm sm:text-base break-all">{user?.phone || 'Not provided'}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bio
                </label>
                {isEditing ? (
                  <>
                    <textarea
                      {...register('bio')}
                      rows="4"
                      className={`w-full px-3 sm:px-4 py-2 bg-white rounded-lg shadow-neomorph-inset border ${
                        errors.bio ? 'border-red-300' : 'border-primary-100/30'
                      } focus:outline-none focus:ring-2 focus:ring-primary-500/20 resize-none text-sm sm:text-base`}
                      placeholder="Tell us about yourself..."
                    />
                    {errors.bio && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.bio.message}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500 text-right">
                      {user?.bio?.length || 0}/500 characters
                    </p>
                  </>
                ) : (
                  <div className="p-3 bg-gray-50 rounded-lg shadow-neomorph-inset">
                    <p className="text-gray-700 text-sm sm:text-base break-words">
                      {user?.bio || 'No bio added yet'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Business Interests Card */}
          <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-6 border border-primary-100/30">
            <div className="flex items-center mb-4 sm:mb-6">
              <div className="p-2 bg-purple-50 rounded-lg mr-3">
                <Briefcase className="h-5 w-5 text-purple-600" />
              </div>
              <h2 className="text-base sm:text-lg font-semibold text-gray-800">Business Interests</h2>
            </div>

            {isEditing ? (
              <>
                <div className="flex flex-wrap gap-2 mb-4">
                  {businessTagsOptions.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      className={`inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                        selectedTags.includes(tag)
                          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 shadow-neomorph-inset'
                      }`}
                    >
                      <Tag className="h-3 w-3 mr-1" />
                      {tag}
                    </button>
                  ))}
                </div>
                <p className="text-xs sm:text-sm text-gray-500">
                  Select tags that match your business interests
                </p>
              </>
            ) : (
              <div className="flex flex-wrap gap-2">
                {user?.businessTags?.length > 0 ? (
                  user.businessTags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium bg-primary-50 text-primary-700 border border-primary-200"
                    >
                      <Tag className="h-3 w-3 mr-1" />
                      {tag}
                    </span>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No business interests selected</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Profile Photo & Stats */}
        <div className="lg:w-80 space-y-4 sm:space-y-6 lg:order-2">
          {/* Profile Photo Card */}
          <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-6 border border-primary-100/30">
            <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 sm:mb-6 flex items-center">
              <div className="w-1 h-4 sm:h-5 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
              Profile Photo
            </h2>
            
            <div className="flex flex-col items-center">
              <div className="relative mb-4">
                <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg shadow-primary-500/30 flex items-center justify-center overflow-hidden">
                  {(previewUrl || user?.avatar) ? (
                    <img
                      src={previewUrl || user?.avatar}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white font-bold text-2xl sm:text-4xl">
                      {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                    </span>
                  )}
                </div>
                
                {isEditing && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <div className="absolute -bottom-2 -right-2 flex space-x-2">
                      <button
                        onClick={triggerFileInput}
                        disabled={uploading}
                        className="p-2 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all disabled:opacity-50"
                        title="Upload new photo"
                      >
                        {uploading ? (
                          <Loader className="h-4 w-4 text-primary-600 animate-spin" />
                        ) : (
                          <Upload className="h-4 w-4 text-primary-600" />
                        )}
                      </button>
                      
                      {user?.avatar && (
                        <button
                          onClick={handleDeleteAvatar}
                          disabled={uploading}
                          className="p-2 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all disabled:opacity-50"
                          title="Remove photo"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
              <p className="text-sm text-gray-500 text-center break-words">
                {user?.firstName} {user?.lastName}
              </p>
              {isEditing && (
                <p className="text-xs text-gray-400 mt-2 text-center">
                  Max: 5MB • JPEG, PNG, GIF, WEBP
                </p>
              )}
            </div>
          </div>

          {/* Account Information Card */}
          <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-6 border border-primary-100/30">
            <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 sm:mb-6 flex items-center">
              <div className="w-1 h-4 sm:h-5 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
              Account Information
            </h2>
            
            <div className="space-y-3 sm:space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                  <span className="text-sm text-gray-600">Member Since</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {formatDate(user?.createdAt)}
                </span>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <Shield className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                  <span className="text-sm text-gray-600">Account Type</span>
                </div>
                <span className={`text-sm font-medium capitalize ${
                  user?.role === 'ADMIN' ? 'text-red-600' : 'text-primary-600'
                }`}>
                  {user?.role?.toLowerCase()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;